import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
	FileText, Banknote, Users, Brain, CheckCircle2, Send, ArrowRight,
} from "lucide-react"
import { DashboardLayout } from "@/components/common/DashboardLayout"
import { LoadingSpinner } from "@/components/common/LoadingSpinner"
import { SourceBadge } from "@/components/common/SourceBadge"
import { api } from "@/lib/apiService"

interface SourceStats {
	total: number
	open: number
	scored: number
	avg_score: number | null
	high_relevance: number
}

interface ContactStats {
	total: number
	tender: number
	grant: number
	verified: number
	synced: number
}

interface OverviewResponse {
	success: boolean
	data: {
		tenders: SourceStats
		grants: SourceStats
		contacts: ContactStats
	}
}

function formatNumber(n: number): string {
	return new Intl.NumberFormat("es-ES").format(n)
}

function StatCard({
	icon: Icon,
	iconClass,
	label,
	value,
	sublines,
}: {
	icon: typeof FileText
	iconClass: string
	label: string
	value: string
	sublines: { label: string; value: string }[]
}) {
	return (
		<div className="rounded-xl border border-gray-100 bg-white p-5">
			<div className="flex items-center gap-3 mb-3">
				<div className={`h-9 w-9 rounded-lg flex items-center justify-center ${iconClass}`}>
					<Icon className="h-4 w-4" />
				</div>
				<div>
					<p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
					<p className="text-2xl font-semibold text-gray-900 leading-tight">{value}</p>
				</div>
			</div>
			<dl className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100">
				{sublines.map((s) => (
					<div key={s.label}>
						<dt className="text-[11px] text-muted-foreground">{s.label}</dt>
						<dd className="text-sm font-medium text-gray-800">{s.value}</dd>
					</div>
				))}
			</dl>
		</div>
	)
}

function SourceCard({
	type,
	stats,
	href,
}: {
	type: "tender" | "grant"
	stats: SourceStats
	href: string
}) {
	const isGrant = type === "grant"
	return (
		<Link
			to={href}
			className={`group rounded-xl border p-6 transition-colors ${
				isGrant
					? "border-amber-100 bg-amber-50/40 hover:border-amber-200"
					: "border-emerald-100 bg-emerald-50/40 hover:border-emerald-200"
			}`}
		>
			<div className="flex items-start justify-between mb-4">
				<div className="flex items-center gap-2">
					<SourceBadge type={type} size="md" />
				</div>
				<ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
			</div>
			<p className="text-3xl font-semibold text-gray-900">{formatNumber(stats.total)}</p>
			<p className="text-xs text-muted-foreground mt-1">
				{formatNumber(stats.open)} obertes · {formatNumber(stats.scored)} avaluades
			</p>
			<div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-200/60">
				<div>
					<p className="text-[11px] text-muted-foreground flex items-center gap-1">
						<Brain className="h-3 w-3" /> Score mitjà
					</p>
					<p className="text-base font-medium text-gray-900">
						{stats.avg_score !== null ? stats.avg_score.toFixed(1) : "—"}
					</p>
				</div>
				<div>
					<p className="text-[11px] text-muted-foreground flex items-center gap-1">
						<CheckCircle2 className="h-3 w-3" /> Score ≥ 70
					</p>
					<p className="text-base font-medium text-gray-900">{formatNumber(stats.high_relevance)}</p>
				</div>
			</div>
		</Link>
	)
}

export default function DashboardPage() {
	const [data, setData] = useState<OverviewResponse["data"] | null>(null)
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		api.get<OverviewResponse>("/stats/overview")
			.then((res) => setData(res.data))
			.catch(() => {})
			.finally(() => setIsLoading(false))
	}, [])

	return (
		<DashboardLayout>
			<div className="max-w-6xl mx-auto w-full space-y-6">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
					<p className="text-sm text-muted-foreground mt-1">
						Resum global d'oportunitats comercials i contactes
					</p>
				</div>

				{isLoading ? (
					<div className="flex justify-center py-16">
						<LoadingSpinner size="lg" />
					</div>
				) : !data ? (
					<p className="text-muted-foreground">No s'ha pogut carregar el dashboard.</p>
				) : (
					<div className="space-y-6">
						{/* Per-source cards */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<SourceCard type="tender" stats={data.tenders} href="/" />
							<SourceCard type="grant" stats={data.grants} href="/grants" />
						</div>

						{/* Contact stats split by source */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<StatCard
								icon={Users}
								iconClass="bg-[#2D8A5E]/10 text-[#2D8A5E]"
								label="Contactes totals"
								value={formatNumber(data.contacts.total)}
								sublines={[
									{ label: "De licitacions", value: formatNumber(data.contacts.tender) },
									{ label: "De subvencions", value: formatNumber(data.contacts.grant) },
								]}
							/>
							<StatCard
								icon={CheckCircle2}
								iconClass="bg-emerald-100 text-emerald-700"
								label="Emails verificats"
								value={formatNumber(data.contacts.verified)}
								sublines={[
									{
										label: "% del total",
										value:
											data.contacts.total > 0
												? `${Math.round((data.contacts.verified / data.contacts.total) * 100)}%`
												: "—",
									},
									{ label: "Pendents", value: formatNumber(data.contacts.total - data.contacts.verified) },
								]}
							/>
							<StatCard
								icon={Send}
								iconClass="bg-blue-100 text-blue-700"
								label="Sincronitzats a Pipedrive"
								value={formatNumber(data.contacts.synced)}
								sublines={[
									{
										label: "% del total",
										value:
											data.contacts.total > 0
												? `${Math.round((data.contacts.synced / data.contacts.total) * 100)}%`
												: "—",
									},
									{ label: "Per sincronitzar", value: formatNumber(data.contacts.verified - data.contacts.synced) },
								]}
							/>
						</div>

						{/* Quick actions */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
							<Link
								to="/contacts"
								className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
							>
								<div className="flex items-center gap-3">
									<Users className="h-4 w-4 text-muted-foreground" />
									<span className="text-sm font-medium text-gray-900">Contactes</span>
								</div>
								<ArrowRight className="h-4 w-4 text-muted-foreground" />
							</Link>
							<Link
								to="/outreach"
								className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
							>
								<div className="flex items-center gap-3">
									<Send className="h-4 w-4 text-muted-foreground" />
									<span className="text-sm font-medium text-gray-900">Campanyes outreach</span>
								</div>
								<ArrowRight className="h-4 w-4 text-muted-foreground" />
							</Link>
							<Link
								to="/grants"
								className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
							>
								<div className="flex items-center gap-3">
									<Banknote className="h-4 w-4 text-muted-foreground" />
									<span className="text-sm font-medium text-gray-900">Subvencions</span>
								</div>
								<ArrowRight className="h-4 w-4 text-muted-foreground" />
							</Link>
						</div>
					</div>
				)}
			</div>
		</DashboardLayout>
	)
}
